import { useState } from "react";
import { Navigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, Users, DollarSign, FileStack, ShieldCheck, Mail, Building2, Gavel, Star, BarChart3 } from "lucide-react";
import AdminFeatured from "@/components/admin/AdminFeatured";
import AdminTraffic from "@/components/admin/AdminTraffic";
import AdminOverview from "@/components/admin/AdminOverview";
import AdminUsers from "@/components/admin/AdminUsers";
import AdminRevenue from "@/components/admin/AdminRevenue";
import AdminForms from "@/components/admin/AdminForms";
import AdminCampaigns from "@/components/admin/AdminCampaigns";
import AdminBusiness from "@/components/admin/AdminBusiness";
import AdminAttorneys from "@/components/admin/AdminAttorneys";

const NAV = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "users", label: "Users", icon: Users },
  { id: "revenue", label: "Revenue", icon: DollarSign },
  { id: "forms", label: "Forms", icon: FileStack },
  { id: "business", label: "Business Lookup", icon: Building2 },
  { id: "attorneys", label: "Attorneys", icon: Gavel },
  { id: "featured", label: "Featured & Invites", icon: Star },
  { id: "traffic", label: "Search Traffic", icon: BarChart3 },
  { id: "campaigns", label: "Campaigns", icon: Mail },
];

export default function AdminPanel() {
  const { user, loading } = useAuth();
  const [section, setSection] = useState("overview");

  if (loading || user === null) return <div className="min-h-screen grid place-items-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen bg-secondary/30">
      <Navbar />
      <div className="max-w-[1400px] mx-auto flex flex-col lg:flex-row gap-0 lg:gap-8 px-4 sm:px-6 py-8">
        <aside className="lg:w-64 shrink-0 mb-6 lg:mb-0" data-testid="admin-sidebar">
          <div className="lg:sticky lg:top-24">
            <p className="overline text-accent mb-4 flex items-center gap-2"><ShieldCheck size={14} /> Admin Portal</p>
            <nav className="flex lg:flex-col gap-1 overflow-x-auto">
              {NAV.map((n) => {
                const Icon = n.icon;
                const active = section === n.id;
                return (
                  <button key={n.id} onClick={() => setSection(n.id)} data-testid={`admin-nav-${n.id}`}
                    className={`flex items-center gap-3 px-4 py-2.5 rounded-sm text-sm font-semibold whitespace-nowrap transition-colors ${active ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-secondary"}`}>
                    <Icon size={17} /> {n.label}
                  </button>
                );
              })}
            </nav>
          </div>
        </aside>

        <main className="flex-1 min-w-0">
          {section === "overview" && <AdminOverview />}
          {section === "users" && <AdminUsers currentUserId={user.user_id} />}
          {section === "revenue" && <AdminRevenue />}
          {section === "forms" && <AdminForms />}
          {section === "business" && <AdminBusiness />}
          {section === "attorneys" && <AdminAttorneys />}
          {section === "featured" && <AdminFeatured />}
          {section === "traffic" && <AdminTraffic />}
          {section === "campaigns" && <AdminCampaigns />}
        </main>
      </div>
    </div>
  );
}
